// TanStack Query hooks. Read-only. Mutations live in mutations.ts.
//
// Query-key convention: [entity, ...id?]. Mutations invalidate by entity
// prefix so any subquery refetches without careful bookkeeping at the
// call site.
import { useQuery } from "@tanstack/react-query";
import type {
  AnalyticsDashboard,
  EditHistoryEntry,
  EnrichedFeature,
  FeatureFamily,
  FeatureFlag,
  FeedItem,
  PromptRow,
  PublishStatus,
  Release,
  StagingSummary,
  UnifiedRelease,
  WorkspaceInfo,
} from "@release-hub/shared";
import { api } from "./client";

export function usePrompts() {
  return useQuery({
    queryKey: ["prompts"],
    queryFn: () => api<PromptRow[]>("/api/prompts"),
  });
}

export function useFeed() {
  return useQuery({
    queryKey: ["feed"],
    queryFn: () => api<FeedItem[]>("/api/feed"),
  });
}

/** All squapi flags. Cached longer — squapi is the source of truth and
 *  most consumers just need name/rollout state. */
export function useFlags() {
  return useQuery({
    queryKey: ["squapi", "flags"],
    queryFn: () => api<FeatureFlag[]>("/api/squapi/flags"),
    staleTime: 5 * 60_000,
  });
}

/** Workspace id → name lookup. Used by FlagChip to resolve raw workspace ids. */
export function useWorkspaces() {
  return useQuery({
    queryKey: ["squapi", "workspaces"],
    queryFn: () => api<WorkspaceInfo[]>("/api/squapi/workspaces"),
    staleTime: 10 * 60_000,
  });
}

export function useFeatures() {
  return useQuery({
    queryKey: ["feature-entries"],
    queryFn: () => api<EnrichedFeature[]>("/api/feature-entries"),
  });
}

export function useFeatureFamilies() {
  return useQuery({
    queryKey: ["feature-families"],
    queryFn: () => api<FeatureFamily[]>("/api/feature-families"),
  });
}

export function useFeature(id: string | undefined) {
  return useQuery({
    queryKey: ["feature-entries", id],
    queryFn: () => api<EnrichedFeature>(`/api/feature-entries/${id}`),
    enabled: !!id,
    // While a freshly-created launch is seeding its fields from pasted notes
    // in the background, poll so they appear as soon as they're ready. Stops
    // once seeding_at clears. Mirrors useRelease.
    refetchInterval: (query) => (query.state.data?.seeding_at ? 2000 : false),
  });
}

export function useFlag(id: string | undefined) {
  return useQuery({
    queryKey: ["squapi", "flags", id],
    queryFn: () => api<FeatureFlag>(`/api/squapi/flags/${encodeURIComponent(id!)}`),
    enabled: !!id,
  });
}

/** Cache summary shown on the Releases page (row counts + last-refresh time). */
export function useStagingSummary() {
  return useQuery({
    queryKey: ["staging", "summary"],
    queryFn: () => api<StagingSummary>("/api/staging/summary"),
  });
}

/** Every github_release LEFT JOIN'd with its curated release. Powers the
 *  merged Releases page + the Add-build picker. Un-converted rows have
 *  `release_id: null`.
 *
 *  Key is ["releases", "unified"] — deliberately NOT ["releases", "all"],
 *  which useReleases() (no status) uses for a DIFFERENT shape (Release[]).
 *  Sharing that key made the two clobber each other in cache, which emptied
 *  the Add-build picker. Both still sit under the ["releases"] prefix so
 *  mutation invalidations reach them. */
export function useAllReleases() {
  return useQuery({
    queryKey: ["releases", "unified"],
    queryFn: () => api<UnifiedRelease[]>("/api/releases/all"),
  });
}

/** List curated releases. `status` filter is passed through to the server. */
export function useReleases(status?: PublishStatus) {
  return useQuery({
    queryKey: ["releases", "list", status ?? "all"],
    queryFn: () =>
      api<Release[]>("/api/releases", {
        query: status ? { status } : undefined,
      }),
  });
}

/** Edit-history entries for one entity, newest-first. Editor-only; the
 *  server rejects viewer/sales tokens. Skips the query when either arg is
 *  missing so consumers can be lazy about drawer state. staleTime is 0 so
 *  invalidateQueries() from a write mutation refetches an open drawer
 *  immediately. */
export function useEditHistory(
  entityType: string | undefined,
  entityId: string | undefined,
) {
  return useQuery({
    queryKey: ["edit-history", entityType, entityId],
    queryFn: () =>
      api<EditHistoryEntry[]>("/api/history", {
        query: { entity_type: entityType!, entity_id: entityId! },
      }),
    enabled: !!entityType && !!entityId,
  });
}

/** Editor-only analytics rollup — one request returns the whole
 *  dashboard payload. Defaults to a 30-day window. */
export function useAnalyticsDashboard(days = 30) {
  return useQuery({
    queryKey: ["analytics-dashboard", days],
    queryFn: () =>
      api<AnalyticsDashboard>("/api/analytics/dashboard", {
        query: { days: String(days) },
      }),
  });
}

export function useRelease(id: string | undefined) {
  return useQuery({
    queryKey: ["releases", "detail", id],
    // Server returns the release joined with github_releases fields + items.
    // /api/releases returns all; filter to the id we want. There isn't a
    // single-item endpoint yet, so this is a small compromise.
    queryFn: async () => {
      const list = await api<Release[]>("/api/releases");
      return list.find((r) => r.id === id) ?? null;
    },
    enabled: !!id,
    // While a fresh Convert is seeding narrative + items in the background,
    // poll so they appear as soon as they're ready. Stops once seeding_at
    // clears (or the release is gone).
    refetchInterval: (query) => (query.state.data?.seeding_at ? 2000 : false),
  });
}
